import React from 'react';
import { Link } from 'react-router-dom';

const TermsConditions = () => {
    return (
        <div className="max-w-4xl mx-auto py-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 sm:p-8 lg:p-12">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">Terms & Conditions</h1>
                <p className="text-gray-600 dark:text-gray-400 mb-8">Last updated: January 2025</p>

                <div className="prose prose-gray max-w-none">
                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">1. Acceptance of Terms</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            By accessing and using RewriteKing ("the Service"), you accept and agree to be bound by these Terms
                            and Conditions. If you do not agree with any part of these terms, you must not use our service.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">2. Description of Service</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            RewriteKing is a web-based text rewriting tool that helps users transform casual messages into
                            professional communications. The service uses AI technology to rewrite text according to selected
                            tones (professional, formal, friendly, concise, or empathetic).
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">3. Acceptable Use</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            You agree to use RewriteKing only for lawful purposes and in accordance with these Terms. You agree
                            not to:
                        </p>
                        <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mb-4">
                            <li>Use the service to create or transmit any content that is illegal, harmful, threatening, abusive, or offensive</li>
                            <li>Use the service to violate any laws or regulations</li>
                            <li>Attempt to gain unauthorized access to the service or its related systems</li>
                            <li>Interfere with or disrupt the service or servers connected to the service</li>
                            <li>Use automated systems or scripts to access the service without permission</li>
                            <li>Reproduce, duplicate, copy, or exploit any portion of the service without express written permission</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">4. User Content and Ownership</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            You retain all ownership rights to any text content you submit to RewriteKing. By using our service,
                            you grant us a limited, non-exclusive license to process your content solely for the purpose of
                            providing the rewriting service. We do not claim ownership of your content.
                        </p>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            <strong className="text-gray-900 dark:text-gray-100">Important:</strong> We do not store, save, or
                            retain your text content. Your input is processed in real-time and immediately discarded after
                            processing.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">5. Service Availability</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            We strive to provide continuous access to RewriteKing, but we do not guarantee that the service will
                            be available at all times. The service may be unavailable due to maintenance, technical issues, or
                            other reasons beyond our control. We reserve the right to modify, suspend, or discontinue the service
                            at any time without prior notice.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">6. Accuracy and Quality</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            While we strive to provide accurate and high-quality rewritten text, RewriteKing is provided "as is"
                            without warranties of any kind. We do not guarantee that the rewritten content will be error-free,
                            grammatically perfect, or suitable for your specific needs. You are responsible for reviewing and
                            verifying any rewritten content before using it.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">7. Intellectual Property</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            The RewriteKing website, including its design, functionality, and underlying technology, is owned by
                            RewriteKing and protected by copyright and other intellectual property laws. You may not copy,
                            modify, distribute, or create derivative works based on the service without our express written
                            permission.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">8. Limitation of Liability</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            To the fullest extent permitted by law, RewriteKing and its operators shall not be liable for any
                            indirect, incidental, special, consequential, or punitive damages, or any loss of profits or
                            revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other
                            intangible losses resulting from your use of the service.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">9. Changes to Terms</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            We reserve the right to modify these Terms & Conditions at any time. We will update the "Last updated"
                            date at the top of this page when changes are made. Your continued use of the service after any
                            changes constitutes acceptance of the new terms.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">10. Governing Law</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            These Terms & Conditions shall be governed by and construed in accordance with applicable laws,
                            without regard to its conflict of law provisions.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-4">11. Contact Us</h2>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            If you have any questions about these Terms & Conditions, please contact us at{' '}
                            <a href="mailto:contact@rewriteking.com" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300">
                                contact@rewriteking.com
                            </a>
                        </p>
                    </section>
                </div>

                <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
                    <Link to="/" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium">
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default TermsConditions;

